import React, { useState, useRef, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
	useAddPartnerCategoryMutation,
	useUpdatePartnerCategoryMutation,
	PartnerCategory,
} from "src/store/services/partnerApi";
import { useUploadImageMutation } from "src/store";

interface AddCategoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	onAddCategory?: () => void;
	editCategory?: PartnerCategory | null;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
	isOpen,
	onClose,
	onAddCategory,
	editCategory,
}) => {
	const [addPartnerCategory, { isLoading }] = useAddPartnerCategoryMutation();
	const [updatePartnerCategory, { isLoading: isUpdating }] =
		useUpdatePartnerCategoryMutation();
	const [uploadImage, { isLoading: isUploading }] = useUploadImageMutation();
	const [error, setError] = useState<string | null>(null);

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [iconFile, setIconFile] = useState<File | null>(null);
	const [iconPreview, setIconPreview] = useState<string | null>(null);
	const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const isEditMode = !!editCategory;

	// Populate form when editing
	useEffect(() => {
		if (isOpen && editCategory) {
			setName(editCategory.category_name || "");
			setDescription(editCategory.category_description || "");
			setExistingImageUrl(editCategory.category_image_url || null);
			setIconPreview(editCategory.category_image_url || null);
			setIconFile(null);
		} else if (isOpen && !editCategory) {
			// Reset form for add mode
			setName("");
			setDescription("");
			setIconFile(null);
			setIconPreview(null);
			setExistingImageUrl(null);
		}
		setError(null);
	}, [isOpen, editCategory]);

	const handleImageUpload = async (file: File): Promise<string | null> => {
		try {
			const formData = new FormData();
			formData.append("image", file);
			const uploadResponse = await uploadImage(formData).unwrap();
			return uploadResponse.payload.url;
		} catch (err) {
			console.error("Image upload failed:", err);
			return null;
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!name.trim()) {
			setError("Category name is required");
			return;
		}

		try {
			let imageUrl = "";

			// Upload image if provided
			if (iconFile) {
				const uploadedUrl = await handleImageUpload(iconFile);
				if (!uploadedUrl) {
					setError("Failed to upload image. Please try again.");
					return;
				}
				imageUrl = uploadedUrl;
			}

			// Add or update category
			if (isEditMode && editCategory) {
				await updatePartnerCategory({
					id: editCategory.category_id,
					data: {
						name: name.trim(),
						description: description.trim(),
						image_url: imageUrl || existingImageUrl || "https://local",
					},
				}).unwrap();
			} else {
				await addPartnerCategory({
					name: name.trim(),
					description: description.trim(),
					image_url: imageUrl || "https://local", // Fallback if no image
				}).unwrap();
			}

			// Reset form
			setName("");
			setDescription("");
			setIconFile(null);
			setIconPreview(null);
			setError(null);

			// Call callback if provided
			if (onAddCategory) {
				onAddCategory();
			}

			onClose();
		} catch (err: any) {
			console.error("Failed to add category:", err);
			setError(
				err?.data?.message || "Failed to add category. Please try again."
			);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setIconFile(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setIconPreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	if (!isOpen) return null;

	return (
		<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
			<div className='bg-white rounded-lg w-full max-w-md'>
				<div className='flex justify-between items-center p-4 border-b'>
					<h2 className='text-xl font-semibold text-gray-800'>
						{isEditMode ? "Edit Category" : "Add New Category"}
					</h2>
					<button
						onClick={onClose}
						className='text-gray-500 hover:text-gray-700'
						aria-label='Close modal'
					>
						<CloseIcon fontSize='small' />
					</button>
				</div>

				<form onSubmit={handleSubmit} className='p-6'>
					<div className='mb-4'>
						<label
							htmlFor='name'
							className='block text-sm font-medium text-gray-700 mb-1'
						>
							Category Name <span className='text-red-500'>*</span>
						</label>
						<input
							type='text'
							id='name'
							value={name}
							onChange={(e) => setName(e.target.value)}
							className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent'
							placeholder='Enter category name'
							required
						/>
					</div>

					<div className='mb-4'>
						<label
							htmlFor='description'
							className='block text-sm font-medium text-gray-700 mb-1'
						>
							Description
						</label>
						<textarea
							id='description'
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
							className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent'
							placeholder='Enter a brief description'
						/>
					</div>

					<div className='mb-6'>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							Category Icon
						</label>
						<input
							type='file'
							ref={fileInputRef}
							onChange={handleFileChange}
							accept='image/*'
							className='hidden'
						/>
						<div
							onClick={triggerFileInput}
							className='mt-1 flex flex-col items-center justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:bg-gray-50 transition-colors'
						>
							{iconPreview ? (
								<div className='flex flex-col items-center'>
									<img
										src={iconPreview}
										alt='Preview'
										className='h-16 w-16 object-contain mb-2 rounded'
									/>
									<p className='text-sm text-gray-500'>
										{isEditMode
											? "Click to change icon"
											: "Click to change icon"}
									</p>
								</div>
							) : (
								<div className='flex flex-col items-center'>
									<div className='h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center mb-2'>
										<svg
											className='h-6 w-6 text-gray-500'
											fill='none'
											viewBox='0 0 24 24'
											stroke='currentColor'
										>
											<AddIcon />
										</svg>
									</div>
									<p className='text-sm text-gray-500'>
										<span className='font-medium text-purple-600'>
											Upload an icon
										</span>{" "}
										or drag and drop
									</p>
									<p className='text-xs text-gray-400 mt-1'>
										PNG, JPG, SVG up to 2MB
									</p>
								</div>
							)}
						</div>
					</div>

					{error && (
						<div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
							<p className='text-sm text-red-600'>{error}</p>
						</div>
					)}

					<div className='flex justify-end space-x-3 pt-2'>
						<button
							type='button'
							onClick={onClose}
							disabled={isLoading || isUpdating || isUploading}
							className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed'
						>
							Cancel
						</button>
						<button
							type='submit'
							disabled={!name.trim() || isLoading || isUpdating || isUploading}
							className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
								!name.trim() || isLoading || isUpdating || isUploading
									? "bg-purple-300 cursor-not-allowed"
									: "bg-purple-600 hover:bg-purple-700"
							}`}
						>
							{isLoading || isUpdating || isUploading
								? isEditMode
									? "Updating..."
									: "Adding..."
								: isEditMode
								? "Update Category"
								: "Add Category"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default AddCategoryModal;
